import React, { useContext, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { FormList, formList, useForm, yupResolver } from "@mantine/form";
import { Container, Grid, Space } from "@mantine/core";
import dayjs from "dayjs";
import { Navigate } from "react-router-dom";
import { UseFormReturnType } from "@mantine/form/lib/use-form";
import SurveyValidationSchema from "../services/validator/SurveyValidationSchema";
import { HttpClientError } from "../../../providers/HttpClient";
import { AuthContext } from "../../../providers/AuthProvider";
import useUpdateSurveyApiService from "../services/api/UpdateSurveyApiService";
import useCreateSurveyApiService from "../services/api/CreateSurveyApiService";
import Survey from "../services/model/Survey";
import SurveyQuestionForm from "./SurveyQuestionForm";
import SurveyDetailsForm from "./SurveyDetailsForm";
import { queryClient } from "../../../App";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";

type SurveyFormProps = {
  onSuccess?: () => void;
  onFailure?: () => void;
  values?: Survey;
};

export type SurveyFormInterface = UseFormReturnType<{
  title: string;
  status: string;
  tags: string[];
  scheduledStartDate: Date;
  scheduledEndDate: Date;
  questions: FormList<{
    id: number | undefined;
    content: string;
    question_type: string;
    choices: { id: number | undefined; content: string }[];
  }>;
}>;

const SurveyForm: React.FC<SurveyFormProps> = ({
  onSuccess,
  onFailure,
  values,
}) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();

  // Check if segmentation feature is enabled for surveys
  const segmentsEnabled = useEnabledFeatureFlag(FeatureFlags.SEGMENTS);
  const segmentsSurveysEnabled = useEnabledFeatureFlag(
    FeatureFlags.SEGMENTS_SURVEYS
  );
  const showSegmentationToggle = segmentsEnabled && segmentsSurveysEnabled;

  // State for targeting type and segments
  const [targetingType, setTargetingType] = useState<"tags" | "segments">(
    values?.targeting_type || "tags"
  );
  const [segmentIds, setSegmentIds] = useState<string[]>(
    values?.segments ? values.segments.map((s) => s.id.toString()) : []
  );

  const today = new Date();
  const expireDate = dayjs(today).add(2, "month").toDate();
  const {
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    isError: isCreateFailure,
    error: createError,
    mutate: onCreate,
  } = useCreateSurveyApiService();

  const {
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
    isError: isUpdateFailure,
    error: updateError,
    mutate: onUpdate,
  } = useUpdateSurveyApiService();

  const showErrorMessage = (err: AxiosError | null): void =>
    showToast({
      strapiId: "survey_submission_failed",
      data: { message: HttpClientError(err) },
    });

  if (!auth) {
    return <Navigate replace to="/login" />;
  }
  useEffect(() => {
    if (isCreateFailure) {
      showErrorMessage(createError);
      if (onFailure) {
        onFailure();
      }
    }

    if (isUpdateFailure) {
      showErrorMessage(updateError);
      if (onFailure) {
        onFailure();
      }
    }
  }, [isCreateFailure, createError, updateError, isUpdateFailure]);

  const showSuccessMessage = (message: string): void =>
    showToast({
      strapiId: "survey_submission_success",
      data: { message },
    });

  useEffect(() => {
    if (isCreateSuccess) {
      queryClient.invalidateQueries(["ListSurveyApiService", 1]).then(() => {
        showSuccessMessage("Your survey has been created successfully.");
        if (onSuccess) {
          onSuccess();
        }
      });
    }

    if (isUpdateSuccess) {
      queryClient
        .invalidateQueries(["SurveyDetailsApiService", values?.id])
        .then(() => {
          queryClient
            .invalidateQueries(["ListSurveyApiService", auth.employer.id])
            .then(() => {
              showSuccessMessage("Your survey has been updated successfully.");
              if (onSuccess) {
                onSuccess();
              }
            });
        });
    }
  }, [isUpdateSuccess, isCreateSuccess]);

  const form = useForm({
    schema: yupResolver(SurveyValidationSchema),
    initialValues: {
      title: values?.title || "",
      status: values?.status || "draft",
      tags: values?.tags ? values.tags.map((it) => it.id.toString()) : [],
      scheduledStartDate: values?.scheduled_start_date
        ? dayjs(values?.scheduled_start_date).toDate()
        : today,
      scheduledEndDate: values?.scheduled_end_date
        ? dayjs(values?.scheduled_end_date).toDate()
        : expireDate,
      questions: formList(
        values?.questions
          ? values.questions
              .filter((it) => !it.is_deleted)
              .map((it) => ({
                id: it.id ? it.id : undefined,
                content: it.content,
                question_type: it.question_type,
                choices: it.choices
                  .filter((k) => !k.is_deleted)
                  .map((choice) => ({
                    id: choice.id ? choice.id : undefined,
                    content: choice.content,
                  })),
              }))
          : []
      ),
    },
  });

  const onSubmitHandler = (formValues: typeof form.values): void => {
    const {
      title,
      status,
      tags: tag,
      scheduledStartDate,
      scheduledEndDate,
      questions,
    } = formValues;
    const tags = tag.map((it) => Number(it));

    // Prepare request data with targeting type
    const requestData = {
      title,
      status,
      targeting_type: targetingType,
      tags: targetingType === "tags" ? tags : [],
      segments:
        targetingType === "segments" ? segmentIds.map((it) => Number(it)) : [],
      scheduled_start_date: scheduledStartDate.toISOString(),
      scheduled_end_date: scheduledEndDate.toISOString(),
    };

    if (values) {
      onUpdate({
        id: values.id,
        ...requestData,
        questions: questions.map((it) => ({
          ...it,
          choices: it.choices.filter((k) => !k.id),
        })),
      });
    } else {
      onCreate({
        ...requestData,
        questions,
        employer_id: auth.employer.id,
        created_by_id: auth.id,
      });
    }
  };

  const handleSaveDraft = (): void => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      onSubmitHandler({ ...form.values, status: "draft" });
    }
  };

  const handlePublish = (): void => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      onSubmitHandler({ ...form.values, status: "published" });
    }
  };

  return (
    <form style={{ width: "100%" }}>
      <Grid
        columns={24}
        style={{ backgroundColor: "#f7f7f8", padding: 0, margin: 0 }}
      >
        <Grid.Col span={19}>
          <Space
            style={{
              paddingTop: "40px",
            }}
          />
          <Container size={600}>
            <SurveyQuestionForm form={form} values={values} />
          </Container>
        </Grid.Col>
        <Grid.Col
          style={{
            height: "100vh",
            backgroundColor: "#ffffff",
            padding: "0 24px",
            maxWidth: "33.333333333333336%",
            position: "fixed",
            right: "0",
          }}
          span={5}
        >
          <SurveyDetailsForm
            form={form}
            isLoading={isCreateLoading || isUpdateLoading}
            showSegmentationToggle={showSegmentationToggle}
            targetingType={targetingType}
            setTargetingType={setTargetingType}
            segmentIds={segmentIds}
            setSegmentIds={setSegmentIds}
            onSaveDraft={handleSaveDraft}
            onPublish={handlePublish}
          />
        </Grid.Col>
      </Grid>
      <Space h="xl" />
    </form>
  );
};

export default SurveyForm;
